import { Container, Graphics, Sprite } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONT, SCREEN, SHAPE } from '../config/tokens';
import type { Layout, Screen } from '../render/stage';
import { frame } from '../assets/loader';
import { dogFrame } from '../assets/names';
import { button, iconButton, label, roundedPanel, titleChip } from '../ui/components';
import { openModal } from '../ui/modal';
import { DOGS, type DogInfo } from '../meta/dogs';
import type { Nav } from '../meta/nav';
import { dogsFound } from '../meta/progress';
import { sound } from '../audio/sound';

const CARD = { w: 424, h: 252, xs: [64, 536], ys: [288, 556, 824, 1092] };

/**
 * Mockup 29: "My Dogs" – 2 × 4 cards. Found dogs show their portrait and
 * open a detail card; missing ones are cocoa silhouettes with a "?" and
 * where they will be found.
 */
export class DogCollectionScreen implements Screen {
  readonly view = new Container();
  private readonly bg = new Graphics();
  private readonly content = new Container();
  private readonly overlay = new Container();
  private readonly back: Container;
  private l?: Layout;

  constructor(nav: Nav) {
    const save = nav.save();
    const title = titleChip('My Dogs');
    title.position.set(SCREEN.width / 2, 150);
    const count = label(`${dogsFound(save).length} of ${DOGS.length} found`, FONT.size.body, { weight: FONT.weight.button });
    count.position.set(SCREEN.width / 2, 232);
    this.content.addChild(title, count);
    DOGS.forEach((d, i) => {
      const card = this.card(d, !!save.dogs[d.id], save.nextLevel);
      card.position.set(CARD.xs[i % 2], CARD.ys[Math.floor(i / 2)]);
      this.content.addChild(card);
    });
    const home = button('Back to Café', 420, 'secondary', () => nav.showCafe(), 84);
    home.position.set(SCREEN.width / 2, 1420);
    this.content.addChild(home);
    this.back = iconButton('back', () => nav.showCafe());
    this.back.position.set(100, 150);
    this.content.addChild(this.back);
    this.view.addChild(this.bg, this.content, this.overlay);
  }

  private card(d: DogInfo, found: boolean, nextLevel: number): Container {
    const c = new Container();
    c.addChild(roundedPanel(CARD.w, CARD.h, { fill: found ? 'cream' : 'latte', radius: 24 }));
    // portrait: the happy pose, cropped to head and shoulders
    const inner = new Container();
    const s = new Sprite(frame(dogFrame(d.id, 'happy')));
    s.anchor.set(0.5, 0);
    s.scale.set(330 / s.texture.height);
    s.position.set(CARD.w / 2, 14);
    const mask = new Graphics().roundRect(8, 8, CARD.w - 16, CARD.h - 70, 18).fill(0xffffff);
    s.mask = mask;
    inner.addChild(s, mask);
    c.addChild(inner);
    const strip = new Graphics().roundRect(4, CARD.h - 60, CARD.w - 8, 56, 20).fill(COLORS.latte);
    c.addChild(strip);
    if (found) {
      const name = label(d.name, FONT.size.cardTitle, { weight: FONT.weight.heading });
      name.position.set(CARD.w / 2, CARD.h - 32);
      const badge = new Container();
      badge.addChild(new Graphics().circle(0, 0, 26).fill(COLORS.butter).stroke({ width: SHAPE.outline, color: COLORS.cocoa }));
      const paw = new Sprite(frame('obstacle_paw_stain'));
      paw.anchor.set(0.5);
      paw.scale.set(30 / paw.texture.width);
      paw.tint = COLORS.cocoa;
      badge.addChild(paw);
      badge.position.set(CARD.w - 40, 40);
      c.addChild(name, badge);
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.on('pointertap', () => this.detail(d));
    } else {
      s.tint = COLORS.cocoa;
      const q = label('?', 90, { weight: FONT.weight.heading, color: 'cream' });
      q.position.set(CARD.w / 2, 105);
      const where = d.location === 'tokyo' ? 'Tokyo' : d.location === 'paris' ? 'Paris' : `After level ${d.arrivesAfterLevel}`;
      const t = label(where, FONT.size.cardTitle - 2, { weight: FONT.weight.button });
      t.position.set(CARD.w / 2, CARD.h - 32);
      c.addChild(q, t);
      void nextLevel;
    }
    return c;
  }

  private detail(d: DogInfo): void {
    sound.play('praise', { pitch: 1.2 });
    const m = openModal(this.overlay, this.l, 760, 1000);
    const pose = new Sprite(frame(dogFrame(d.id, 'happy')));
    pose.anchor.set(0.5, 1);
    const sc = 480 / pose.texture.height;
    pose.scale.set(sc);
    pose.position.set(380, 560);
    gsap.fromTo(pose.scale, { x: sc * 0.9, y: sc * 0.9 }, { x: sc, y: sc, duration: 0.4, ease: 'back.out(2)' });
    const name = label(d.name, FONT.size.celebration, { weight: FONT.weight.heading });
    name.position.set(380, 620);
    const breed = label(d.breed, FONT.size.secondary, { weight: FONT.weight.body });
    breed.position.set(380, 680);
    const lines = label(d.lines.join('\n'), FONT.size.secondary, { weight: FONT.weight.body });
    lines.style.align = 'center';
    lines.position.set(380, 790);
    const close = iconButton('close', () => m.close(), undefined, 76);
    close.position.set(700, 60);
    const ok = button('Back', 360, 'secondary', () => m.close(), 84);
    ok.position.set(380, 920);
    m.panel.addChild(pose, name, breed, lines, ok, close);
  }

  layout(l: Layout): void {
    this.l = l;
    this.bg.clear().rect(l.left, 0, l.right - l.left, l.bottom).fill(COLORS.mint);
    // cards sit in the 1024 × 1536 reference; centre the block on taller screens
    const extra = Math.max(0, l.safeBottom - l.safeTop - (1480 - 92));
    this.content.y = l.safeTop - 92 + extra / 3;
  }
}
